// import React from 'react';
import { styled } from '@mui/material/styles';
import { sortBy } from 'lodash';
import Typography from '@mui/material/Typography';
import { SdgScoreClobDto, ReportAppendix } from 'models/dtoTypes';
import SubSection from './_subSection';
import DoNoSignificantHarmClob from './_doNoSignificantHarmClob';
import ReportAppendixClob from './_reportAppendixClob';
import Empty from './_empty';
import { sdgSvg } from 'kosmos/images';
import { useTranslation } from 'react-i18next';

const PREFIX = 'DoNoSignificantHarmReport';

const classes = {
  item: `${PREFIX}-item`,
  content: `${PREFIX}-content`,
  btnContainer: `${PREFIX}-btnContainer`,
};

const Root = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  [`& .${classes.item}`]: {
    width: '100%',
    marginBottom: 20,
  },
  [`& .${classes.content}`]: {
    position: 'relative',
  },
  [`& .${classes.btnContainer}`]: {
    position: 'absolute',
    right: 0,
    top: 10,
    zIndex: 100,
  },
}));

interface IProps {
  sdgReport: SdgScoreClobDto[];
  ghgScopeSupplyChainAppendixReport: ReportAppendix[];
}

export default function Appendix(props: IProps): JSX.Element {
  const { sdgReport, ghgScopeSupplyChainAppendixReport } = props;
  const { t } = useTranslation('default');
  const renderUnsdgSimpleItem = (item: SdgScoreClobDto, index: number) => {
    const { sdgNumber, title, affinity, associatedNaceCodes } = item;
    const key = title ? title.toString().replace(/\W/g, '-').toLowerCase() : '';
    // to reuse DoNoSignificantHarmClob style ui
    // set anchor={`${key}-content`} to go back to sdgReport
    return (
      <div key={index} className={classes.item}>
        <DoNoSignificantHarmClob
          sdgId={`${key}-appendix`}
          title={`${sdgNumber || 0}. ${title || ''}`}
          affinity={affinity}
          associatedNaceCodes={associatedNaceCodes || []}
          autoHeight={true}
        />
      </div>
    );
  };

  const renderGhgSimpleItem = (item: ReportAppendix, index: number) => {
    const { title, affinity, details } = item;
    const detailList = Object.values(details || {});
    const key: string = title ? title.toString().replace(/\W/g, '-').toLowerCase() : '';
    return (
      <div key={index} className={classes.item}>
        <ReportAppendixClob
          sdgId={`${key}-appendix`}
          title={title || ''}
          affinity={affinity}
          detailList={detailList || []}
          autoHeight={true}
        />
      </div>
    );
  };
  const renderUnsdgsContent = () => {
    const sdgHasAppendix = sdgReport.filter(
      (s) => s.associatedNaceCodes && s.associatedNaceCodes.length > 0,
    );
    if (sdgHasAppendix.length === 0) {
      return <Empty title={t('there-is-no-data-available')} />;
    }

    return <div>{sdgHasAppendix.map(renderUnsdgSimpleItem)}</div>;
  };

  const renderGhgContent = () => {
    const ghgAppendix = sortBy(ghgScopeSupplyChainAppendixReport, (i) => i.sortOrder);
    if (ghgAppendix.length === 0) {
      return <Empty title={t('there-is-no-data-available')} />;
    }

    return <div>{ghgAppendix.map(renderGhgSimpleItem)}</div>;
  };

  return (
    <Root>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', marginBottom: 4 }}>
        {t('appendix')}
      </Typography>
      <div className={classes.content}>
        <div id="unsdg-breakdown-appendix"></div>
        <SubSection
          title={t('united-nations-sustainable-development-goals-sdgs-scoring-breakdown')}
          icon={<img src={sdgSvg} />}
        >
          {renderUnsdgsContent()}
        </SubSection>
        <div id="ghg-appendix"></div>
        <SubSection title={t('ghg-scope-3-supply-chains')} icon={<img src={sdgSvg} />}>
          {renderGhgContent()}
        </SubSection>
      </div>
    </Root>
  );
}
